import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  ClipboardList,
  FileText,
  MessageSquare,
  ShieldAlert,
  UserMinus,
  Users,
  DoorOpen,
  Sparkles,
} from 'lucide-react';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { useIncidentes } from '../../api/hooks/useIncidentes';
import { usePresencasDiarias } from '../../api/hooks/usePresencasDiarias';
import { useEventosAcesso } from '../../api/hooks/useEventosAcesso';
import { useCrud } from '../../api/hooks/useCrud';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { useAuthStore } from '../../store/authStore';
import { hmFromDate } from '../../components/chamada';
import { AlunosEmRiscoCard } from '../../components/evasao/AlunosEmRiscoCard';
import { SetupWizard } from '../../components/onboarding/SetupWizard';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { useEscolaDashboardRealtime } from '../../hooks/useEscolaDashboardRealtime';
import { isJustificativaAtiva } from '../admin/justificativaEscopo';
import type { Turma, TurnoOperacional } from '../../types/models';
import { TurnoAoVivoPanel } from './TurnoAoVivoPanel';
import { useTurnoAoVivo } from './useTurnoAoVivo';
import '../../components/ui/datatable.css';
import './equipe-dashboard.css';

type JustificativaRow = {
  id: string;
  status: string;
  dataFalta: string;
  aluno?: { nome?: string } | null;
};

type AlertaCoberturaRow = {
  id: string;
  status: string;
};

type FilaTone = 'hot' | 'warn' | 'soft' | 'neutral';

type FilaItem = {
  key: string;
  label: string;
  value: number;
  hint: string;
  to: string;
  tone: FilaTone;
  icon: ReactNode;
};

function normalizeCargo(cargo?: string | null): 'SECRETARIA' | 'COORDENACAO' | 'OUTRO' {
  const c = (cargo || '').toUpperCase();
  if (c === 'SECRETARIA') return 'SECRETARIA';
  if (c === 'COORDENACAO' || c === 'COORDENADOR' || c.includes('COORD')) {
    return 'COORDENACAO';
  }
  return 'OUTRO';
}

type AlunoComResp = {
  id: string;
  ativo?: boolean;
  turma?: { id?: string } | null;
  responsaveis?: Array<{
    responsavel?: {
      telefone?: string | null;
      email?: string | null;
      ativo?: boolean;
    } | null;
  }>;
};

function alunoCadastroIncompleto(a: AlunoComResp): boolean {
  if (a.ativo === false) return false;
  const semTurma = !a.turma?.id;
  const resps = a.responsaveis || [];
  const temContato = resps.some((ra) => {
    const r = ra.responsavel;
    if (!r || r.ativo === false) return false;
    return !!(r.telefone?.trim() || r.email?.trim());
  });
  return semTurma || !temContato;
}

export function EquipeDashboard() {
  const navigate = useNavigate();
  const cargoRaw = useAuthStore((s) => s.cargo);
  const escolaId = useAuthStore((s) => s.escolaId);
  const cargoKind = normalizeCargo(cargoRaw);
  const isSec = cargoKind === 'SECRETARIA';
  const isCoord = cargoKind === 'COORDENACAO';

  const { hojeStr, isSimulation, clockReady, isSyncing } = useHojeReferencia();
  const { virtualEpochMs } = useSimClock({
    pollMs: isSimulation ? 2_000 : 15_000,
  });
  const dayReady = clockReady && /^\d{4}-\d{2}-\d{2}$/.test(hojeStr);
  const pollSim = isSimulation ? 10_000 : false;
  const horaAtual = hmFromDate(
    isSimulation ? new Date(virtualEpochMs) : new Date(),
  );

  const { prontoParaOperar } = useSetupStatus({
    routePrefix: '/equipe',
    cargo: cargoRaw || undefined,
  });
  const mostrarWizard = !prontoParaOperar;

  useEscolaDashboardRealtime(escolaId);

  const { data: turmas } = useCrud<Turma>('turmas');
  const { data: turnosOp = [] } = useCrud<TurnoOperacional>('turnos');
  const { incidentes, abertos, criticos } = useIncidentes();

  /** Listagem CRUD de alunos NÃO traz responsáveis/telefone — usa endpoint com vínculo. */
  const { data: cadastroStats } = useQuery({
    queryKey: ['equipe-dash-cadastros', escolaId],
    queryFn: async () => {
      if (!escolaId) return { incompletos: 0, semTurma: 0, semContato: 0, total: 0 };
      const { data } = await api.get(ROUTES.alunos.comResponsaveis(escolaId));
      const list = (data?.alunos ?? []) as AlunoComResp[];
      let semTurma = 0;
      let semContato = 0;
      let incompletos = 0;
      for (const a of list) {
        const noTurma = !a.turma?.id;
        const temContato = (a.responsaveis || []).some((ra) => {
          const r = ra.responsavel;
          if (!r || r.ativo === false) return false;
          return !!(r.telefone?.trim() || r.email?.trim());
        });
        if (noTurma) semTurma += 1;
        if (!temContato) semContato += 1;
        if (alunoCadastroIncompleto(a)) incompletos += 1;
      }
      return {
        incompletos,
        semTurma,
        semContato,
        total: list.length,
      };
    },
    enabled: !!escolaId,
    staleTime: 60_000,
  });
  const { presencas } = usePresencasDiarias({
    data: dayReady ? hojeStr : undefined,
    enabled: dayReady,
    refetchIntervalMs: pollSim,
  });
  const { eventos } = useEventosAcesso({
    data: dayReady ? hojeStr : undefined,
    enabled: dayReady,
    refetchIntervalMs: pollSim,
  });
  const { data: justificativas = [] } = useQuery({
    queryKey: ['equipe-dash-justificativas', escolaId],
    queryFn: async () => {
      const { data } = await api.get(
        `/escolas/${escolaId}/presencas-diarias/justificativas`,
      );
      return (Array.isArray(data) ? data : data?.data ?? []) as JustificativaRow[];
    },
    enabled: !!escolaId,
    staleTime: 30_000,
  });

  const { data: alertasCobertura = [] } = useQuery({
    queryKey: ['equipe-dash-cobertura-alertas', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.cobertura.alertas(escolaId!));
      return (Array.isArray(data) ? data : data?.data ?? []) as AlertaCoberturaRow[];
    },
    enabled: !!escolaId,
    staleTime: 30_000,
  });

  const welcomeTexts: Record<string, { title: string; subtitle: string }> = {
    COORDENACAO: {
      title: 'Olá, Coordenador(a)!',
      subtitle:
        'O Diretor já iniciou a configuração da escola. Veja as etapas que precisam da sua atenção.',
    },
    SECRETARIA: {
      title: 'Olá, Secretário(a)!',
      subtitle:
        'A equipe está configurando a escola. Sua tarefa principal é matricular os alunos nas turmas já criadas.',
    },
  };
  const welcomeInfo =
    welcomeTexts[cargoKind === 'SECRETARIA' ? 'SECRETARIA' : 'COORDENACAO'];

  const turmasAtivas = useMemo(() => {
    const turnoById = new Map((turnosOp || []).map((t) => [t.id, t]));
    return (turmas || [])
      .filter((t) => t.ativo)
      .map((t) => ({
        ...t,
        turnoOperacional:
          t.turnoOperacional ||
          (t.turnoOperacionalId
            ? turnoById.get(t.turnoOperacionalId)
            : undefined),
      }));
  }, [turmas, turnosOp]);

  const turnoAoVivo = useTurnoAoVivo({
    escolaId,
    hojeStr,
    horaAtual,
    turmas: turmasAtivas,
    enabled: dayReady && !mostrarWizard,
    pollMs: pollSim === false ? 15_000 : pollSim,
  });

  const presentesHoje = presencas.filter(
    (p) => p.status === 'PRESENTE' || p.status === 'ATRASADO',
  ).length;
  const ausentesHoje = presencas.filter((p) => p.status === 'AUSENTE').length;
  const entradas = eventos.filter(
    (e) => e.tipoEvento === 'ENTRADA' || e.tipoEvento === 'ENTRADA_MANUAL',
  ).length;
  const saidas = eventos.filter(
    (e) => e.tipoEvento === 'SAIDA' || e.tipoEvento === 'SAIDA_MANUAL',
  ).length;
  const dentroEscola = Math.max(0, entradas - saidas);
  const portariaCount = entradas + saidas;

  /** KPI alinhado ao Turno ao vivo (só turmas sem nenhuma chamada + slot perdido). */
  const turmasSemChamada = turnoAoVivo.kpis.turmasSemChamada;
  const turmasParciais = turnoAoVivo.kpis.turmasParciais;
  const turmasComChamada = turnoAoVivo.kpis.turmasComRegistro;
  const turmasAguardandoHorario = turnoAoVivo.kpis.turmasAguardando;

  const justifPendentes = useMemo(
    () =>
      justificativas.filter(
        (j) =>
          j.status === 'PENDENTE' && isJustificativaAtiva(j, hojeStr),
      ),
    [justificativas, hojeStr],
  );

  const coberturaPendentes = useMemo(
    () => alertasCobertura.filter((a) => a.status === 'PENDENTE').length,
    [alertasCobertura],
  );

  const cadastrosIncompletos = cadastroStats?.incompletos ?? 0;
  const cadastroHint =
    cadastroStats && cadastroStats.incompletos > 0
      ? [
          cadastroStats.semTurma > 0
            ? `${cadastroStats.semTurma} s/ turma`
            : null,
          cadastroStats.semContato > 0
            ? `${cadastroStats.semContato} s/ contato`
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'Pendências de cadastro'
      : 'Turma e contato ok';

  const temRegistroPedagogico = presentesHoje + ausentesHoje > 0;
  const portariaSemChamada =
    portariaCount > 0 && (turmasSemChamada > 0 || !temRegistroPedagogico);

  const filaTotal =
    justifPendentes.length +
    turmasSemChamada +
    cadastrosIncompletos +
    coberturaPendentes +
    abertos;

  const filaItems: FilaItem[] = useMemo(() => {
    const justif: FilaItem = {
      key: 'justif',
      label: 'Justificativas',
      value: justifPendentes.length,
      hint: 'Em análise / prazo ativo',
      to: '/equipe/justificativas',
      tone: justifPendentes.length > 0 ? 'hot' : 'neutral',
      icon: <FileText size={14} />,
    };
    const chamadasItem: FilaItem = {
      key: 'chamadas',
      label: 'Turmas s/ chamada',
      value: turmasSemChamada,
      hint:
        turmasAtivas.length === 0
          ? 'Sem turmas ativas'
          : turmasSemChamada > 0
            ? `Sem nenhuma chamada` +
              (turmasParciais > 0 ? ` · ${turmasParciais} parcial(is)` : '') +
              (turmasComChamada > 0 ? ` · ${turmasComChamada} ok` : '')
            : turmasParciais > 0
              ? `${turmasParciais} parcial(is) · ${turmasComChamada} com registro`
              : turmasAguardandoHorario > 0
                ? `Nenhuma atrasada · ${turmasAguardandoHorario} ainda no horário`
                : turmasComChamada > 0
                  ? `${turmasComChamada} com registro`
                  : 'Aguardando grade do dia',
      to: '/equipe/chamadas-gestao',
      tone: turmasSemChamada > 0 ? 'hot' : turmasParciais > 0 ? 'warn' : 'neutral',
      icon: <ClipboardList size={14} />,
    };
    const cadastro: FilaItem = {
      key: 'cadastro',
      label: 'Cadastros',
      value: cadastrosIncompletos,
      hint: cadastroHint,
      to: '/equipe/alunos',
      tone: cadastrosIncompletos > 0 ? 'warn' : 'neutral',
      icon: <Users size={14} />,
    };
    const cobertura: FilaItem = {
      key: 'cobertura',
      label: 'Cobertura',
      value: coberturaPendentes,
      hint: 'Slots sem substituto',
      to: '/equipe/cobertura',
      tone: coberturaPendentes > 0 ? 'warn' : 'neutral',
      icon: <UserMinus size={14} />,
    };
    const incidentesItem: FilaItem = {
      key: 'incidentes',
      label: 'Incidentes',
      value: abertos,
      hint: criticos > 0 ? `${criticos} crítico(s)` : 'Abertos',
      to: '/equipe/incidentes',
      tone: abertos > 0 ? (criticos > 0 ? 'hot' : 'warn') : 'neutral',
      icon: <ShieldAlert size={14} />,
    };
    const atendimento: FilaItem = {
      key: 'atendimento',
      label: 'Atendimento',
      value: 0,
      hint: 'Conversas com famílias',
      to: '/equipe/conversas-pais',
      tone: 'soft',
      icon: <MessageSquare size={14} />,
    };

    // Secretaria: cadastro + justificativa no centro; Coord: chamada + cobertura + risco operacional
    if (isSec) {
      return [justif, cadastro, chamadasItem, cobertura, atendimento, incidentesItem];
    }
    if (isCoord) {
      return [chamadasItem, cobertura, justif, incidentesItem, cadastro, atendimento];
    }
    return [chamadasItem, justif, cobertura, cadastro, incidentesItem, atendimento];
  }, [
    justifPendentes.length,
    turmasSemChamada,
    turmasParciais,
    turmasComChamada,
    turmasAguardandoHorario,
    turmasAtivas.length,
    cadastrosIncompletos,
    cadastroHint,
    coberturaPendentes,
    abertos,
    criticos,
    isSec,
    isCoord,
  ]);

  const cargoTitle = isSec
    ? 'Painel da Secretaria'
    : isCoord
      ? 'Painel da Coordenação'
      : 'Painel da Equipe';

  const cargoSubtitle = isSec
    ? 'Fila de trabalho do dia — frequência, cadastro e atendimento às famílias'
    : isCoord
      ? 'Fila de trabalho do dia — chamadas, cobertura e acompanhamento pedagógico'
      : 'Fila de trabalho do dia';

  const dataLabel = dayReady
    ? new Date(`${hojeStr}T12:00:00`).toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : isSyncing
      ? 'Sincronizando dia simulado…'
      : '—';

  const dataCurta = dayReady
    ? new Date(`${hojeStr}T12:00:00`).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '…';

  const insight = portariaSemChamada
    ? 'Há movimento na portaria, mas ainda faltam chamadas/registros pedagógicos. Priorize cobrir turmas pendentes ou reconciliar frequência.'
    : filaTotal === 0
      ? 'Fila limpa por enquanto — acompanhe portaria e novas justificativas ao longo do turno.'
      : null;

  const incidentesAbertos = incidentes.filter((i) => {
    const st = String(i.status || '').toUpperCase();
    return st === 'ABERTO' || st === 'EM_ANALISE';
  });

  return (
    <div className="admin-page">
      <div className="eq-dash" data-tour="page.equipe.dashboard">
        {mostrarWizard && (
          <div
            style={{
              marginBottom: 16,
              padding: '14px 20px',
              borderRadius: 14,
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '1.5px solid #3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.12)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(29, 78, 216, 0.3)',
                }}
              >
                <Sparkles size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#1e40af' }}>
                  {welcomeInfo.title}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#1d4ed8', marginTop: 2, lineHeight: 1.4 }}>
                  {welcomeInfo.subtitle}
                </div>
              </div>
            </div>
          </div>
        )}

        <header className="eq-dash-header" data-tour="page.hero">
          <div>
            <h1>{cargoTitle}</h1>
            <p>
              {cargoSubtitle}
              <br />
              <span style={{ textTransform: 'capitalize' }}>{dataLabel}</span>
            </p>
              <div className="eq-dash-date-row">
                <span
                  className={`eq-dash-date-chip${isSimulation ? ' is-sim' : ''}`}
                  title={
                    isSimulation
                      ? 'Data da sessão de simulação (cockpit)'
                      : 'Calendário civil'
                  }
                >
                  <CalendarDays size={12} aria-hidden />
                  <span>{isSimulation ? 'Dia simulado' : 'Dia atual'}</span>
                  <strong>{dataCurta}</strong>
                </span>
                {isSyncing && (
                  <span className="eq-dash-sync">Aguardando relógio da sessão…</span>
                )}
              </div>
            </div>
            <span
              className={`eq-dash-pill${
                !dayReady ? '' : filaTotal > 0 ? ' is-alert' : ' is-ok'
              }`}
            >
              {!dayReady
                ? 'Sincronizando…'
                : filaTotal > 0
                  ? `${filaTotal} pendência(s) na fila`
                  : 'Fila do dia em dia'}
            </span>
          </header>

          <section data-tour="page.kpis" aria-label="Fila do dia">
            <div className="eq-fila">
              {filaItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`eq-fila-card${
                    item.tone === 'hot'
                      ? ' is-hot'
                      : item.tone === 'warn'
                        ? ' is-warn'
                        : item.tone === 'soft'
                          ? ' is-soft'
                          : ''
                  }`}
                  onClick={() => navigate(item.to)}
                >
                  <span className="eq-fila-label">
                    {item.icon} {item.label}
                  </span>
                  <span className="eq-fila-value">{item.value}</span>
                  <span className="eq-fila-hint">{item.hint}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="eq-ctx" data-tour="page.ctx" aria-label="Contexto do dia">
            <div className="eq-ctx-item">
              <span>Na escola agora</span>
              <strong>{dentroEscola}</strong>
            </div>
            <div className="eq-ctx-item">
              <span>Presentes</span>
              <strong>{presentesHoje}</strong>
            </div>
            <div className="eq-ctx-item">
              <span>Ausentes</span>
              <strong style={{ color: ausentesHoje > 0 ? '#b91c1c' : undefined }}>
                {ausentesHoje}
              </strong>
            </div>
            <div className="eq-ctx-item">
              <span>Portaria (hoje)</span>
              <strong>{portariaCount}</strong>
            </div>
          </section>

          {(insight || portariaSemChamada) && (
            <p className={`eq-insight${insight && !portariaSemChamada ? ' is-ok' : ''}`}>
              {insight}
            </p>
          )}

          <section className="eq-panels" data-tour="page.panels">
            <div className="eq-panel eq-panel--turno">
              <TurnoAoVivoPanel
                model={turnoAoVivo}
                horaAtual={horaAtual}
                portariaCount={portariaCount}
                title="Turno ao vivo"
                onOpenGovernanca={() => navigate('/equipe/chamadas-gestao')}
                onOpenPortaria={() => navigate('/equipe/portaria')}
              />
              <div className="eq-action-row">
                <button
                  type="button"
                  className="eq-btn primary"
                  onClick={() => navigate('/equipe/frequencia')}
                >
                  Ver frequência
                </button>
                <button
                  type="button"
                  className="eq-btn"
                  onClick={() => navigate('/equipe/justificativas')}
                >
                  <FileText size={14} /> Justificativas
                  {justifPendentes.length > 0
                    ? ` (${justifPendentes.length})`
                    : ''}
                </button>
              </div>
            </div>

            <div className="eq-col-right">
              <div data-tour="page.risco" className="eq-risco-slot">
                <AlunosEmRiscoCard
                  expanded={isCoord}
                  compact
                  onSelectAluno={(alunoId) =>
                    navigate(`/equipe/alunos/${alunoId}`)
                  }
                />
              </div>

              <div className="eq-panel">
                <div className="eq-panel-head">
                  <h3>Portaria ao vivo</h3>
                  <span className="eq-live">AO VIVO</span>
                </div>
                <div className="eq-list">
                  {eventos.slice(0, 8).map((ev) => {
                    const isEnt = ev.tipoEvento.startsWith('ENTRADA');
                    const hora = (() => {
                      try {
                        return new Date(ev.dataHoraEvento).toLocaleTimeString(
                          'pt-BR',
                          { hour: '2-digit', minute: '2-digit' },
                        );
                      } catch {
                        return '—';
                      }
                    })();
                    return (
                      <div key={ev.id} className="eq-list-row">
                        <div
                          style={{
                            display: 'flex',
                            gap: 6,
                            alignItems: 'center',
                          }}
                        >
                          <span className={`dot ${isEnt ? 'in' : 'out'}`} />
                          <span style={{ fontWeight: 500 }}>
                            {ev.aluno?.nome || ev.codigoLido || 'Aluno'}
                          </span>
                        </div>
                        <span className="eq-list-meta">{hora}</span>
                      </div>
                    );
                  })}
                  {eventos.length === 0 && (
                    <p className="eq-empty">Sem eventos hoje.</p>
                  )}
                </div>
                <div className="eq-action-row">
                  <button
                    type="button"
                    className="eq-btn"
                    onClick={() => navigate('/equipe/portaria')}
                  >
                    <DoorOpen size={14} /> Abrir portaria
                  </button>
                  {isSec && (
                    <button
                      type="button"
                      className="eq-btn"
                      onClick={() => navigate('/equipe/conversas-pais')}
                    >
                      <MessageSquare size={14} /> Atendimento
                    </button>
                  )}
                  {isCoord && (
                    <button
                      type="button"
                      className="eq-btn"
                      onClick={() => navigate('/equipe/cobertura')}
                    >
                      <UserMinus size={14} /> Cobertura
                      {coberturaPendentes > 0 ? ` (${coberturaPendentes})` : ''}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {justifPendentes.length > 0 && (
            <div className="eq-panel" data-tour="page.justif">
              <div className="eq-panel-head">
                <h3>Justificativas para analisar</h3>
                <button
                  type="button"
                  className="eq-panel-link"
                  onClick={() => navigate('/equipe/justificativas')}
                >
                  Ver todas →
                </button>
              </div>
              <div className="eq-list">
                {justifPendentes.slice(0, 5).map((j) => (
                  <div key={j.id} className="eq-list-row">
                    <span style={{ fontWeight: 600 }}>
                      {j.aluno?.nome || 'Aluno'}
                    </span>
                    <span className="eq-list-meta">
                      falta {j.dataFalta?.slice(0, 10) || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {incidentesAbertos.length > 0 && (
            <div
              className="eq-panel"
              style={{ padding: 0, overflow: 'hidden' }}
              data-tour="page.incidentes"
            >
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--border-color, #e5e7eb)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ShieldAlert size={18} style={{ color: '#dc2626' }} />
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                    Incidentes abertos
                  </h3>
                </div>
                <button
                  type="button"
                  className="eq-panel-link"
                  style={{ marginRight: 12 }}
                  onClick={() => navigate('/equipe/incidentes')}
                >
                  Abrir →
                </button>
              </div>
              <div className="dt-container">
                <table className="dt-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Severidade</th>
                      <th>Descrição</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidentesAbertos.slice(0, 8).map((inc) => {
                      const tipoLabel = String(
                        inc.tipoIncidente ?? inc.tipo ?? '—',
                      ).replace(/_/g, ' ');
                      return (
                      <tr key={inc.id} className="dt-row">
                        <td style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {tipoLabel}
                        </td>
                        <td>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              background:
                                inc.severidade === 'CRITICA'
                                  ? '#fee2e2'
                                  : inc.severidade === 'ALTA'
                                    ? '#fef3c7'
                                    : '#f3f4f6',
                              color:
                                inc.severidade === 'CRITICA'
                                  ? '#991b1b'
                                  : inc.severidade === 'ALTA'
                                    ? '#92400e'
                                    : '#6b7280',
                            }}
                          >
                            {inc.severidade}
                          </span>
                        </td>
                        <td
                          style={{
                            fontSize: '0.82rem',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {inc.descricao}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                          {(() => {
                            try {
                              return new Date(
                                inc.dataHoraReferencia || inc.criadoEm,
                              ).toLocaleDateString('pt-BR');
                            } catch {
                              return '—';
                            }
                          })()}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
    </div>
  );
}
